import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { toast } from 'sonner';
import type {
  SubscriptionPlan,
  Subscription,
  UsageTracking,
  SmsPackage,
  TenantSmsCredits,
  Invoice,
  PlanId,
  FeatureKey,
  QuotaKey,
  QuotaCheckResult,
  SubscriptionPlanRow,
  SubscriptionRow,
  UsageTrackingRow,
  SmsPackageRow,
  TenantSmsCreditsRow,
  InvoiceRow,
} from '@/types/subscription';
import {
  subscriptionPlanFromRow,
  subscriptionFromRow,
  usageTrackingFromRow,
  smsPackageFromRow,
  tenantSmsCreditsFromRow,
  invoiceFromRow,
} from '@/types/subscription';

interface QuotaCache {
  customers: QuotaCheckResult | null;
  appointments: QuotaCheckResult | null;
  users: QuotaCheckResult | null;
  sms: QuotaCheckResult | null;
}

interface SubscriptionState {
  // Data
  plans: SubscriptionPlan[];
  smsPackages: SmsPackage[];
  currentPlan: SubscriptionPlan | null;
  subscription: Subscription | null;
  usage: UsageTracking | null;
  smsCredits: TenantSmsCredits | null;
  invoices: Invoice[];
  quotaCache: QuotaCache;

  // Loading states
  isLoading: boolean;
  isInitialized: boolean;

  // Tenant reference
  tenantId: string | null;

  // Actions
  initialize: (tenantId: string) => Promise<void>;
  refresh: () => Promise<void>;

  // Data fetchers
  fetchPlans: () => Promise<void>;
  fetchSmsPackages: () => Promise<void>;
  fetchSubscription: () => Promise<void>;
  fetchUsage: () => Promise<void>;
  fetchSmsCredits: () => Promise<void>;
  fetchInvoices: () => Promise<void>;
  fetchQuotas: () => Promise<void>;

  // Feature and quota checks
  hasFeature: (feature: FeatureKey) => boolean;
  checkQuota: (quota: QuotaKey) => QuotaCheckResult;
  getTotalSmsCredits: () => number;

  // Checkout actions
  createCheckoutSession: (type: 'subscription' | 'sms_package', options: {
    planId?: PlanId;
    billingCycle?: 'monthly' | 'yearly';
    smsPackageId?: string;
  }) => Promise<{ url: string } | null>;
  createPortalSession: () => Promise<{ url: string } | null>;
}

export const useSubscriptionStore = create<SubscriptionState>()((set, get) => ({
  // Initial state
  plans: [],
  smsPackages: [],
  currentPlan: null,
  subscription: null,
  usage: null,
  smsCredits: null,
  invoices: [],
  quotaCache: { customers: null, appointments: null, users: null, sms: null },
  isLoading: false,
  isInitialized: false,
  tenantId: null,

  // Initialize store for a tenant
  initialize: async (tenantId: string) => {
    if (get().isInitialized && get().tenantId === tenantId) {
      return;
    }

    set({ isLoading: true, tenantId });

    try {
      // First, fetch plans and SMS packages (no dependencies)
      await Promise.all([
        get().fetchPlans(),
        get().fetchSmsPackages(),
      ]);

      // Then fetch subscription (depends on plans being loaded for currentPlan lookup)
      // and other tenant-specific data in parallel
      await Promise.all([
        get().fetchSubscription(),
        get().fetchUsage(),
        get().fetchSmsCredits(),
        get().fetchInvoices(),
      ]);

      // Fetch real-time quotas (depends on subscription being loaded)
      await get().fetchQuotas();

      set({ isInitialized: true });
    } catch (error) {
      logger.error('Error initializing subscription store', { error, component: 'SubscriptionStore' });
    } finally {
      set({ isLoading: false });
    }
  },

  // Refresh all data
  refresh: async () => {
    const { tenantId } = get();
    if (!tenantId) return;

    set({ isLoading: true });

    try {
      await Promise.all([
        get().fetchSubscription(),
        get().fetchUsage(),
        get().fetchSmsCredits(),
        get().fetchInvoices(),
      ]);

      // Fetch real-time quotas after subscription is loaded
      await get().fetchQuotas();
    } catch (error) {
      logger.error('Error refreshing subscription data', { error, component: 'SubscriptionStore' });
    } finally {
      set({ isLoading: false });
    }
  },

  // Fetch all available plans
  fetchPlans: async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (error) throw error;

      const plans = (data as SubscriptionPlanRow[]).map(subscriptionPlanFromRow);
      set({ plans });
    } catch (error) {
      logger.error('Error fetching subscription plans', { error, component: 'SubscriptionStore' });
    }
  },

  // Fetch SMS packages
  fetchSmsPackages: async () => {
    try {
      const { data, error } = await supabase
        .from('sms_packages')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (error) throw error;

      const smsPackages = (data as SmsPackageRow[]).map(smsPackageFromRow);
      set({ smsPackages });
    } catch (error) {
      logger.error('Error fetching SMS packages', { error, component: 'SubscriptionStore' });
    }
  },

  // Fetch tenant's subscription
  fetchSubscription: async () => {
    const { tenantId, plans } = get();
    if (!tenantId) return;

    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('tenant_id', tenantId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        const subscription = subscriptionFromRow(data as SubscriptionRow);
        const currentPlan = plans.find(p => p.id === subscription.planId) || null;
        set({ subscription, currentPlan });
      } else {
        // No subscription record - check tenant's subscription_plan_id as fallback
        const { data: tenantData } = await supabase
          .from('tenants')
          .select('subscription_plan_id')
          .eq('id', tenantId)
          .single();

        const tenantPlanId = tenantData?.subscription_plan_id || 'free';
        const fallbackPlan = plans.find(p => p.id === tenantPlanId) || plans.find(p => p.id === 'free') || null;
        set({ subscription: null, currentPlan: fallbackPlan });
      }
    } catch (error) {
      logger.error('Error fetching subscription', { error, component: 'SubscriptionStore' });
    }
  },

  // Fetch current month's usage
  fetchUsage: async () => {
    const { tenantId } = get();
    if (!tenantId) return;

    try {
      const currentPeriod = new Date().toISOString().slice(0, 7); // YYYY-MM

      const { data, error } = await supabase
        .from('usage_tracking')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('period', currentPeriod)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        const usage = usageTrackingFromRow(data as UsageTrackingRow);
        set({ usage });
      } else {
        set({ usage: null });
      }
    } catch (error) {
      logger.error('Error fetching usage', { error, component: 'SubscriptionStore' });
    }
  },

  // Fetch SMS credits (uses SECURITY DEFINER RPC to bypass RLS)
  fetchSmsCredits: async () => {
    const { tenantId } = get();
    if (!tenantId) return;

    try {
      const { data, error } = await supabase.rpc('get_sms_credits', {
        p_tenant_id: tenantId,
      });

      if (error) throw error;

      if (data) {
        const row = data as TenantSmsCreditsRow;
        const smsCredits = tenantSmsCreditsFromRow(row);
        set({ smsCredits });
      } else {
        set({ smsCredits: null });
      }
    } catch (error) {
      logger.error('Error fetching SMS credits', { error, component: 'SubscriptionStore' });
    }
  },

  // Fetch invoices
  fetchInvoices: async () => {
    const { tenantId } = get();
    if (!tenantId) return;

    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('invoice_date', { ascending: false })
        .limit(50);

      if (error) throw error;

      const invoices = (data as InvoiceRow[]).map(invoiceFromRow);
      set({ invoices });
    } catch (error) {
      logger.error('Error fetching invoices', { error, component: 'SubscriptionStore' });
    }
  },

  // Fetch real-time quotas from database RPC
  fetchQuotas: async () => {
    const { tenantId } = get();
    if (!tenantId) return;

    try {
      const quotaTypes: QuotaKey[] = ['customers', 'appointments', 'users', 'sms'];
      const results = await Promise.all(
        quotaTypes.map(async (qt) => {
          const { data, error } = await supabase.rpc('check_quota', {
            p_tenant_id: tenantId,
            p_quota_type: qt,
          });
          if (error) {
            logger.error(`Error fetching ${qt} quota`, { error, component: 'SubscriptionStore' });
            return [qt, null] as const;
          }
          return [qt, data as QuotaCheckResult] as const;
        })
      );

      const quotaCache: QuotaCache = { customers: null, appointments: null, users: null, sms: null };
      for (const [qt, result] of results) {
        if (result) {
          // RPC returns JSONB with snake_case keys
          const raw = result as any;
          quotaCache[qt] = {
            allowed: raw.allowed,
            current: raw.current,
            limit: raw.limit,
            quotaType: qt,
            planId: (raw.plan_id || 'free') as PlanId,
          };
        }
      }
      set({ quotaCache });
    } catch (error) {
      logger.error('Error fetching quotas', { error, component: 'SubscriptionStore' });
    }
  },

  // Check if tenant has a specific feature
  hasFeature: (feature: FeatureKey): boolean => {
    const { currentPlan, subscription } = get();

    // No plan means free plan features only
    if (!currentPlan) return false;

    // Grandfathered users have full access
    if (subscription?.isGrandfathered) return true;

    switch (feature) {
      case 'email_reminders':
        return currentPlan.hasEmailReminders;
      case 'excel_export':
        return currentPlan.hasExcelExport;
      case 'pdf_export':
        return currentPlan.hasPdfExport;
      case 'recurring_expenses':
        return currentPlan.hasRecurringExpenses;
      case 'advanced_analytics':
        return currentPlan.hasAdvancedAnalytics;
      case 'custom_branding':
        return currentPlan.hasCustomBranding;
      case 'custom_domain':
        return currentPlan.hasCustomDomain;
      case 'custom_email_domain':
        return currentPlan.hasCustomEmailDomain;
      case 'whitelabel':
        return currentPlan.hasWhitelabel;
      case 'api_access':
        return currentPlan.hasApiAccess;
      case 'multi_location':
        return currentPlan.hasMultiLocation;
      case 'priority_support':
        return currentPlan.hasPrioritySupport;
      case 'online_booking':
        return currentPlan.hasOnlineBooking;
      case 'waiting_list':
        return currentPlan.hasWaitingList;
      case 'customer_portal':
        return currentPlan.hasCustomerPortal;
      case 'google_calendar':
        return currentPlan.hasGoogleCalendar;
      case 'invoicing':
        return currentPlan.hasInvoicing;
      default:
        return false;
    }
  },

  // Check quota limits (uses cached RPC results from fetchQuotas)
  checkQuota: (quota: QuotaKey): QuotaCheckResult => {
    const { currentPlan, quotaCache, subscription } = get();

    const defaultResult: QuotaCheckResult = {
      allowed: true,
      current: 0,
      limit: null,
      quotaType: quota,
      planId: (currentPlan?.id || 'free') as PlanId,
    };

    if (!currentPlan) return defaultResult;

    // Grandfathered users have unlimited quotas
    if (subscription?.isGrandfathered) {
      return { ...defaultResult, limit: null };
    }

    // Use cached RPC result if available
    const cached = quotaCache[quota];
    if (cached) {
      return cached;
    }

    // Fallback to plan limits with 0 current (before quotas are fetched)
    switch (quota) {
      case 'users':
        return { ...defaultResult, limit: currentPlan.maxUsers };
      case 'customers':
        return { ...defaultResult, limit: currentPlan.maxCustomers };
      case 'appointments':
        return { ...defaultResult, limit: currentPlan.maxAppointmentsPerMonth };
      case 'sms':
        const totalCredits = get().getTotalSmsCredits();
        return { ...defaultResult, limit: totalCredits, allowed: totalCredits > 0 };
      default:
        return defaultResult;
    }
  },

  // Get total SMS credits available
  getTotalSmsCredits: (): number => {
    const { smsCredits } = get();
    if (!smsCredits) return 0;
    return smsCredits.creditsRemaining + smsCredits.planCreditsRemaining;
  },

  // Create Stripe checkout session
  createCheckoutSession: async (type, options) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({
          type,
          planId: options.planId,
          billingCycle: options.billingCycle,
          smsPackageId: options.smsPackageId,
          successUrl: type === 'sms_package'
            ? `${window.location.origin}/billing?sms_success=true`
            : type === 'custom_email_domain'
            ? `${window.location.origin}/settings?email_domain_success=true`
            : `${window.location.origin}/billing?success=true`,
          cancelUrl: type === 'custom_email_domain'
            ? `${window.location.origin}/settings?canceled=true`
            : `${window.location.origin}/billing?canceled=true`,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error);
      }

      return { url: result.url };
    } catch (error) {
      logger.error('Error creating checkout session', { error, component: 'SubscriptionStore' });
      toast.error(error instanceof Error ? error.message : 'Σφάλμα κατά τη δημιουργία πληρωμής');
      return null;
    }
  },

  // Create Stripe customer portal session
  createPortalSession: async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({
          returnUrl: `${window.location.origin}/billing`,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error);
      }

      return { url: result.url };
    } catch (error) {
      logger.error('Error creating portal session', { error, component: 'SubscriptionStore' });
      toast.error(error instanceof Error ? error.message : 'Σφάλμα κατά τη διαχείριση συνδρομής');
      return null;
    }
  },
}));
